//@author travis
import { DataAccess } from "../dataaccess/data.access";
import { AddressType } from "../pojo/AddressI";
import { Item } from "../pojo/Item";
import { S25Const } from "../util/s25-const";
import { S25Util } from "../util/s25-util";
import { UserprefService } from "./userpref.service";
import { Cache } from "../decorators/cache.decorator";
import { Proto } from "../pojo/Proto";
import ISODateString = Proto.ISODateString;
import NumericalBoolean = Proto.NumericalBoolean;
import { TemplateRef } from "@angular/core";

export interface TooltipServiceParams {
    itemTypeId: number;
    itemId: number;
    itemId2?: number;
}

export class TooltipService {
    private static TooltipService_Repository = {
        isSeized: false,
    };

    @Cache({ expireTimeMs: 120000, targetName: "TooltipService", immutable: true })
    public static getTooltip<TooltipType extends TooltipResponse = TooltipResponse>({
        itemTypeId,
        itemId,
        itemId2,
    }: TooltipServiceParams): Promise<TooltipType> {
        const type = S25Const.itemType[itemTypeId].type;
        const itemId2Param = S25Util.isUndefined(itemId2) ? "" : S25Const.item_type_map[type].item2 + itemId2;
        return DataAccess.get<TooltipType>(
            DataAccess.injectCaller(
                "/tooltip/" + type + ".json?itemId=" + itemId + "&" + type + "_id=" + itemId + itemId2Param,
                "TooltipService.getTooltip",
            ),
        );
    }

    public static getDisable() {
        return UserprefService.getDisableTooltip();
    }

    public static isSeized() {
        return TooltipService.TooltipService_Repository.isSeized;
    }

    public static setSeized() {
        TooltipService.TooltipService_Repository.isSeized = true;
    }

    public static setFree() {
        TooltipService.TooltipService_Repository.isSeized = false;
    }
}

export type TooltipResponse =
    | EventTooltipResponse
    | OrganizationTooltipResponse
    | ContactTooltipResponse
    | LocationTooltipResponse
    | ResourceTooltipResponse;

export type EventTooltipResponse = {
    item: {
        event_id: number;
        event_name: string;
        rsrv_start_dt?: ISODateString;
        rsrv_end_dt?: ISODateString;
        rows: {
            data: Array<{ name: string } & EventTooltipRow>;
        };
        spaces?: {
            data: {
                space_id: number;
                space_name: string;
                is_hidden?: NumericalBoolean;
            }[];
        };
    };
};

export type OrganizationTooltipResponse = {
    item: {
        item_name: string;
        rows: Array<{
            name: string;
            value: string;
        }>;
    };
};

export type ContactTooltipResponse = {
    item: {
        name: string;
        title: any;
        rows: Array<{
            address?: string;
            addr_type?: AddressType;
            phone?: string;
            fax?: string;
            e_mail?: string;
        }>;
    };
};

export type LocationTooltipResponse = {
    item: {
        room_short: string;
        room_long: string;
        image_id?: number;
        conf_name?: string;
        features?: string;
        layouts?: string;
        capacity?: string;
    };
};

export type ResourceTooltipResponse = {
    item: {
        name: string;
    } & (
        | {
              categoryList: Array<string>;
          }
        | {
              // will be removed in a future version
              categories: string;
          }
    );
};

export type EventTooltipRow =
    | EventTooltipLabelledListRow
    | EventTooltipBulletsRow
    | EventTooltipItemRow
    | EventTooltipRawRow
    | EventTooltipHTMLRow
    | EventTooltipTemplateRow
    | EventEventTimeTooltipRow;

type NestedEventTooltipRow = { nested: true; value: EventTooltipRow } | { nested?: false; value: unknown };
type EventTooltipLabelledListRow = {
    type: typeof eventTooltipRowType.LabelledList;
    value: Array<
        NestedEventTooltipRow & {
            label: string;
        }
    >;
};

type EventTooltipBulletsRow = {
    type: typeof eventTooltipRowType.Bullets;
    value: Array<NestedEventTooltipRow>;
};

type EventTooltipItemRow = {
    type: typeof eventTooltipRowType.Item;
    value: {
        itemTypeId: Item.Ids;
        itemId: number | string;
        itemName?: string;
    };
};

type EventTooltipRawRow = {
    type: typeof eventTooltipRowType.Raw;
    value: string;
};

type EventTooltipHTMLRow = {
    type: typeof eventTooltipRowType.HTML;
    value: string;
};

type EventTooltipTemplateRow = {
    type: typeof eventTooltipRowType.Template;
    value: {
        template: TemplateRef<unknown>;
        context: unknown;
    };
};

type EventEventTimeTooltipRow = {
    type: typeof eventTooltipRowType.EventTime;
};

export const eventTooltipRowType = {
    Template: -4,
    LabelledList: -3,
    Bullets: -2,
    Item: -1, // s25-item-generic
    Raw: 1,
    HTML: 3,
    EventTime: 2,
} as const;
